import { useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { PROBLEM } from '../../utils/constants';
import { fadeUp, spring, stagger, viewportOnce } from '../../utils/animations';

export default function Problem() {
  const [ref, inView] = useInView(viewportOnce);
  const sectionRef = useRef<HTMLElement>(null);

  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ['start end', 'end start'],
  });

  // Subtle parallax on the glow
  const glowY = useTransform(scrollYProgress, [0, 1], [60, -60]);

  return (
    <section ref={sectionRef} className="section-padding relative overflow-hidden">
      {/* Parallax glow */}
      <motion.div
        className="absolute top-0 right-0 w-[500px] h-[500px] bg-gradient-radial-blue opacity-20 gpu"
        style={{ y: glowY }}
      />

      <div className="container-custom relative z-10" ref={ref}>
        {/* Headline + body */}
        <motion.div
          className="max-w-3xl mx-auto text-center mb-20"
          variants={fadeUp}
          initial="hidden"
          animate={inView ? 'visible' : 'hidden'}
          transition={spring}
        >
          <h2 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 text-white">
            {PROBLEM.headline}
          </h2>
          <p className="text-lg text-white/50 leading-relaxed max-w-2xl mx-auto">
            {PROBLEM.body}
          </p>
        </motion.div>

        {/* Stat cards — staggered */}
        <motion.div
          className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto"
          variants={stagger(0.12, 0.3)}
          initial="hidden"
          animate={inView ? 'visible' : 'hidden'}
        >
          {PROBLEM.stats.map((stat, i) => (
            <motion.div
              key={i}
              className="glass-card p-8 text-center group"
              variants={fadeUp}
              transition={spring}
              whileHover={{
                borderColor: 'rgba(255,255,255,0.15)',
                transition: { duration: 0.3 },
              }}
            >
              <div className="text-5xl lg:text-6xl font-bold bg-gradient-brand bg-clip-text text-transparent mb-3 transition-transform duration-500 group-hover:scale-105">
                {stat.value}
              </div>
              <div className="text-white/50 text-sm leading-relaxed">
                {stat.label}
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}
